import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMoleculeStructureCheck, type MoleculeStructureCheck } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { structureIssueMessage } from '../../../components/panels/StructureChecksSection';

const SEVERITY_ICON = { ERROR: 'error', WARNING: 'warning', INFO: 'info' } as const;

/**
 * ADR 0020 D7 (#1106): how the structure stood when PeruseLab checked it -- always shown (owner,
 * Q1 (a)): "No issues", or each finding in the check's own words, a warning confirmed intended
 * saying so ("by you", else "by its author": owner, Q2 (a)), and when it was checked.
 */
export function StructureCheckLine({ moleculeId }: { readonly moleculeId: string }) {
  const { t, i18n } = useTranslation();
  const [result, setResult] = useState<MoleculeStructureCheck | null>(null);

  useEffect(() => {
    let current = true;
    setResult(null);
    getMoleculeStructureCheck(moleculeId)
      .then((answer) => current && setResult(answer))
      .catch(() => current && setResult(null));
    return () => {
      current = false;
    };
  }, [moleculeId]);

  if (!result) return null;
  const checked = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(result.checkedAt));
  return (
    <section
      className="structure-check-line"
      data-testid="structure-check-line"
      aria-labelledby="structure-check-line-heading"
    >
      <h3 id="structure-check-line-heading">{t('moleculePage.structureCheck.heading')}</h3>
      {result.findings.length === 0 ? (
        <p>
          <Icon name="check" size="1rem" /> {t('moleculePage.structureCheck.noIssues')}
        </p>
      ) : (
        <ul>
          {result.findings.map((finding, index) => (
            <li key={`${finding.rule}-${index}`}>
              <Icon name={finding.confirmation ? 'check' : SEVERITY_ICON[finding.severity]} size="1rem" />{' '}
              {structureIssueMessage(t, { ...finding, atomIds: [], bondIds: [] })}
              {finding.confirmation ? (
                <>
                  {' '}
                  {t(
                    finding.confirmation.byYou
                      ? 'moleculePage.structureCheck.confirmedByYou'
                      : 'moleculePage.structureCheck.confirmedByAuthor'
                  )}
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <p className="structure-check-line-when">
        {t('moleculePage.structureCheck.checked', { date: checked, engine: result.engine })}
      </p>
    </section>
  );
}
